import React from "react";
import { TrendingUp, TrendingDown, Minus, CalendarRange } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { cn } from "../../lib/utils";

const METRICS = [
  { key: "views", label: "Views" },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "clicks", label: "CTA clicks" },
  { key: "ctr", label: "Click-through", suffix: "%" },
];

const TREND = {
  gaining: { label: "Gaining momentum", icon: TrendingUp, cls: "border-primary/50 text-primary", body: "This week is up at least 10% on views or clicks versus last week." },
  fading: { label: "Fading", icon: TrendingDown, cls: "border-destructive/60 text-destructive", body: "This week is down at least 10% on views or clicks versus last week." },
  steady: { label: "Steady", icon: Minus, cls: "border-border text-foreground", body: "Traffic is within 10% of last week." },
  no_data: { label: "Waiting for data", icon: CalendarRange, cls: "border-border text-muted-foreground", body: "No visits in the last two weeks yet. Share your campaign link to get started." },
};

const fmt = (v, suffix = "") => `${Number.isInteger(v) ? v : Number(v).toFixed(1)}${suffix}`;

const Delta = ({ pct, abs, suffix = "" }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-muted-foreground">no change</span>;
  }
  const up = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs font-semibold", flat ? "text-muted-foreground" : up ? "text-primary" : "text-destructive")}>
      <Icon className="h-3.5 w-3.5" />
      {up && "+"}
      {pct}% <span className="font-normal text-muted-foreground">({abs > 0 ? "+" : ""}{fmt(abs, suffix)})</span>
    </span>
  );
};

const WeekTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-border bg-card px-3 py-2 text-xs">
      <p className="font-semibold">Week of {label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-muted-foreground">
          {p.name}: <span className="text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

/**
 * Week-over-week comparison + 8-week history.
 * Expects `wow` = { this_week, last_week, comparison, trend } and `weekly` = [{ label, views, clicks, ... }]
 */
const WeeklyReport = ({ wow, weekly = [] }) => {
  if (!wow) return null;
  const trend = TREND[wow.trend] || TREND.steady;
  const TrendIcon = trend.icon;

  return (
    <section className="border border-border bg-card" data-testid="weekly-report">
      <div className="flex flex-col gap-4 border-b border-border px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Weekly report</p>
          <p className="mt-1 text-sm text-muted-foreground">Last 7 days compared with the 7 days before.</p>
        </div>
        <div className={cn("inline-flex items-center gap-2 border px-3 py-2", trend.cls)} data-testid="weekly-trend">
          <TrendIcon className="h-4 w-4" />
          <span className="display text-xl uppercase leading-none tracking-wide">{trend.label}</span>
        </div>
      </div>

      <div className="grid gap-px bg-border/60 sm:grid-cols-2 xl:grid-cols-4">
        {METRICS.map((m) => {
          const c = wow.comparison?.[m.key] || { current: 0, previous: 0, change: 0, change_pct: null };
          return (
            <div key={m.key} className="bg-card p-5" data-testid={`weekly-${m.key}`}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{m.label}</p>
              <div className="mt-3 flex items-end gap-3">
                <p className="display text-4xl leading-none">{fmt(c.current, m.suffix)}</p>
                <p className="pb-0.5 text-xs text-muted-foreground">
                  vs <span className="text-foreground/80">{fmt(c.previous, m.suffix)}</span> last week
                </p>
              </div>
              <div className="mt-2">
                <Delta pct={c.change_pct} abs={c.change} suffix={m.suffix} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 border-t border-border p-5 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Last 8 weeks</p>
          <div className="mt-4 h-52" data-testid="weekly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barGap={2}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <ChartTooltip content={<WeekTip />} cursor={{ fill: "hsl(var(--secondary))" }} />
                <Bar dataKey="views" name="Views" fill="hsl(var(--primary))" radius={0} />
                <Bar dataKey="clicks" name="Clicks" fill="hsl(var(--foreground))" fillOpacity={0.7} radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="flex flex-col justify-center border-l-0 border-border pl-0 lg:col-span-4 lg:border-l lg:pl-6">
          <p className="display text-3xl uppercase leading-none tracking-wide">{trend.label}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{trend.body}</p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
            <div className="border border-border p-3">
              <p className="text-muted-foreground">This week</p>
              <p className="mt-1 font-semibold">
                {wow.this_week.views} views · {wow.this_week.clicks} clicks
              </p>
            </div>
            <div className="border border-border p-3">
              <p className="text-muted-foreground">Last week</p>
              <p className="mt-1 font-semibold">
                {wow.last_week.views} views · {wow.last_week.clicks} clicks
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeeklyReport;
